import React, { useRef, useEffect, useCallback, useState } from 'react';
import {
  Box,
  FormControl,
  TextField,
  Autocomplete,
  Typography,
  Button,
  CircularProgress,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Chip,
  Stack,
  Paper,
  Container,
  IconButton,
  InputAdornment,
  Tooltip,
  alpha
} from '@mui/material';
import {
  CalendarToday,
  Event,
  Group,
  Visibility,
  TrendingUp,
  AssignmentTurnedIn,
  Notes,
  Clear,
  AttachFile,
  Image as ImageIcon,
  PictureAsPdf,
  Code,
  Delete,
  CloudUpload,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  HelpOutline as HelpOutlineIcon,
  RemoveRedEye as MonitorIcon,
  Star as StarIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useClubTheme } from '../../contexts/ClubThemeContext';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import SpeechToTextButton from '../../components/SpeechToTextButton';
import ListboxComponent from '../../components/ListboxComponent ';
import DefensiveVersatilityEditor from '../../components/DefensiveVersatility/DefensiveVersatilityEditor';
import {
  isAcceptedMedia,
  isHtmlFile,
  MAX_MEDIA_SIZE,
  MEDIA_ACCEPT_ATTR,
  MEDIA_FORMATS_LABEL
} from '../../utils/informeMedia';

const CreateInforme = ({
  fecha,
  setFecha,
  evento,
  setEvento,
  equipo,
  setEquipo,
  detalles,
  setDetalles,
  visualizacion,
  setVisualizacion,
  eventos,
  teams,
  existingEquipos,
  markDirty,
  isSubmitting,
  handleCreateCard,
  backendUrl,
  nivel,
  setNivel,
  projection,
  setProjection,
  verdict,
  setVerdict,
  mediaFile,
  setMediaFile,
  fitEstilo,
  setFitEstilo,
  fitFisico,
  setFitFisico,
  fitActitud,
  setFitActitud,
  fitMejora,
  setFitMejora,
  fitContexto,
  setFitContexto,
  statsUrl,
  setStatsUrl,
  defensiveVersatility,
  setDefensiveVersatility
}) => {
  const { t } = useTranslation();
  const { clubName } = useClubTheme();
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  // Considère le contenu Quill vide si pas de texte hors balises (ex: "<p><br></p>")
  const isDetallesEmpty = (html) => {
    if (!html) return true;
    return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim().length === 0;
  };

  // Champs obligatoires (cohérents avec la validation de PlayerCardCreator.handleCreateCard)
  const missingRequired = [
    !fecha && t('playerCardCreator.informeForm.date'),
    !visualizacion && t('playerCardCreator.informeForm.scoutingType'),
    !evento && t('playerCardCreator.informeForm.event'),
    !verdict && t('playerCardCreator.informeForm.finalVerdict'),
    isDetallesEmpty(detalles) && t('playerCardCreator.informeForm.observations')
  ].filter(Boolean);
  const hasMissingRequired = missingRequired.length > 0;

  // Label avec astérisque rouge pour signaler un champ obligatoire
  const requiredLabel = (text) => (
    <span>
      {text}
      <Box component="span" sx={{ color: 'error.main', ml: 0.3 }}>*</Box>
    </span>
  );

  // Génère une preview URL quand un fichier est sélectionné
  useEffect(() => {
    if (mediaFile && mediaFile.type.startsWith('image/')) {
      const url = URL.createObjectURL(mediaFile);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setPreviewUrl(null);
    }
  }, [mediaFile]);

  // Gestionnaire de paste pour les screenshots
  useEffect(() => {
    const handlePaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          const file = item.getAsFile();
          if (file) {
            // Renommer le fichier avec un timestamp
            const extension = file.type.split('/')[1] || 'png';
            const newFile = new File([file], `screenshot_${Date.now()}.${extension}`, {
              type: file.type
            });
            setMediaFile(newFile);
            markDirty();
          }
          break;
        }
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [setMediaFile, markDirty]);

  // Gestionnaire de sélection de fichier
  const handleFileSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!isAcceptedMedia(file)) {
        alert(t('playerCardCreator.informeForm.invalidFileType'));
        return;
      }
      if (file.size > MAX_MEDIA_SIZE) {
        alert(t('playerCardCreator.informeForm.fileTooLarge'));
        return;
      }
      setMediaFile(file);
      markDirty();
    }
  }, [setMediaFile, markDirty, t]);

  // Gestionnaires de drag & drop
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      if (!isAcceptedMedia(file)) {
        alert(t('playerCardCreator.informeForm.invalidFileType'));
        return;
      }
      if (file.size > MAX_MEDIA_SIZE) {
        alert(t('playerCardCreator.informeForm.fileTooLarge'));
        return;
      }
      setMediaFile(file);
      markDirty();
    }
  }, [setMediaFile, markDirty, t]);

  // Supprimer le fichier
  const handleRemoveFile = useCallback(() => {
    setMediaFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [setMediaFile]);

  // Ouvrir le sélecteur de fichier
  const handleClickUpload = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Clés de traduction pour les niveaux VBC
  const vbcRatingKeys = [
    'lowLevelProspect', 'midLevelProspect', 'goodLevelProspect', 'topProspect', 'futureStar',
    'ebaLow', 'ebaHigh', 'lebLow', 'lebHigh', 'acbRotation',
    'bottomBclEurocup', 'midBclEurocup', 'topBclEurocup', 'elMid', 'elTop',
    'nbaRotation', 'nbaAllStar', 'collegeLow', 'collegeMid', 'collegeTop'
  ];

  const compactSectionStyle = {
    background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)',
    backdropFilter: 'blur(8px)',
    borderRadius: 2,
    border: '1px solid rgba(255,255,255,0.3)',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    p: { xs: 1.5, md: 2 },
    mb: { xs: 1.5, md: 2 }
  };

  const iconStyle = {
    mr: 1.5,
    color: 'primary.main',
    fontSize: '1.3rem'
  };

  const getVerdictColor = (value) => {
    switch(value) {
      case 'PRIORITARIO': return 'error';
      case 'MONITORIZAR': return 'warning';
      case 'VOLVER A VER': return 'info';
      case 'DESCARTADO': return 'default';
      case 'DUDAS': return 'secondary';
      default: return 'default';
    }
  };

  const quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['clean']
    ]
  };

  return (
    <Box sx={{ 
      minHeight: { xs: 'auto', md: '40vh' },
      p: 0,
      pb: { xs: 4, md: 0 }
    }}>
      <Box sx={{ 
        width: '100%', 
        margin: 0, 
        padding: 0,
        overflowX: 'hidden'
      }}>

        {/* Section 1: Información Base - Always visible, compact */}
        <Paper elevation={0} sx={compactSectionStyle}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Event sx={iconStyle} />
              <Typography variant="h6" fontWeight={600} sx={{ fontSize: '1.1rem' }}>
                {t('playerCardCreator.informeForm.eventInfo')}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              <Box component="span" sx={{ color: 'error.main', fontWeight: 700, mr: 0.3 }}>*</Box>
              {t('playerCardCreator.informeForm.requiredLegend')}
            </Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <TextField
                  label={requiredLabel(t('playerCardCreator.informeForm.date'))}
                  variant="outlined"
                  type="date"
                  value={fecha}
                  onChange={(e) => {
                    setFecha(e.target.value);
                    markDirty();
                  }}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.5,
                      '&:hover': {
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }
                    }
                  }}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <TextField
                  select
                  label={requiredLabel(t('playerCardCreator.informeForm.scoutingType'))}
                  variant="outlined"
                  value={visualizacion}
                  onChange={(e) => {
                    setVisualizacion(e.target.value);
                    markDirty();
                  }}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.5,
                      '&:hover': {
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }
                    }
                  }}
                >
                  <MenuItem value="">
                    <em>{t('playerCardCreator.informeForm.selectType')}</em>
                  </MenuItem>
                  <MenuItem value="video">📹 {t('playerCardCreator.informeForm.video')}</MenuItem>
                  <MenuItem value="live">🔴 {t('playerCardCreator.informeForm.live')}</MenuItem>
                </TextField>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <Autocomplete
                  freeSolo
                  options={eventos}
                  value={evento || ''}
                  onChange={(event, newValue) => {
                    setEvento(newValue || '');
                    markDirty();
                  }}
                  onInputChange={(event, newInputValue) => {
                    if (event && event.type === 'change') {
                      setEvento(newInputValue);
                      markDirty();
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={requiredLabel(t('playerCardCreator.informeForm.event'))}
                      variant="outlined"
                      placeholder={t('playerCardCreator.informeForm.event')}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1.5,
                          '&:hover': {
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                          }
                        }
                      }}
                    />
                  )}
                  ListboxComponent={ListboxComponent}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <Autocomplete
                  freeSolo
                  options={[...new Set([...(existingEquipos || []), ...(teams || [])])].sort()}
                  getOptionLabel={(option) => option}
                  value={equipo || ''}
                  onChange={(event, newValue) => {
                    setEquipo(newValue || '');
                    markDirty();
                  }}
                  onInputChange={(event, newInputValue) => {
                    if (event && event.type === 'change') {
                      setEquipo(newInputValue);
                      markDirty();
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('playerCardCreator.informeForm.team')}
                      variant="outlined"
                      placeholder={t('playerCardCreator.informeForm.selectOrTypeTeam')}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1.5,
                          '&:hover': {
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                          }
                        }
                      }}
                    />
                  )}
                  ListboxComponent={ListboxComponent}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <TextField
                label={t('playerCardCreator.informeForm.statsUrl')}
                variant="outlined"
                value={statsUrl || ''}
                onChange={(e) => {
                  setStatsUrl(e.target.value);
                  markDirty();
                }}
                placeholder="https://..."
                size="small"
                fullWidth
                InputProps={{
                  endAdornment: statsUrl && (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => { setStatsUrl(''); markDirty(); }}
                        edge="end"
                        size="small"
                      >
                        <Clear />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                    '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }
                  }
                }}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Section 2: Evaluación */}
        <Paper elevation={0} sx={compactSectionStyle}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <TrendingUp sx={iconStyle} />
            <Typography variant="h6" fontWeight={600} sx={{ fontSize: '1.1rem' }}>
              {t('playerCardCreator.informeForm.playerEvaluation')}
            </Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <TextField
                  select
                  label={t('createPlayer.fields.currentVbcRating', { clubName })}
                  variant="outlined"
                  value={nivel || ''}
                  onChange={(e) => {
                    setNivel(e.target.value);
                    markDirty();
                  }}
                  InputProps={{
                    endAdornment: nivel && (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            setNivel('');
                            markDirty();
                          }}
                          edge="end"
                          size="small"
                        >
                          <Clear />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.5,
                      '&:hover': {
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }
                    }
                  }}
                >
                  <MenuItem value="">
                    <em>{t('playerCardCreator.informeForm.selectCurrentLevel')}</em>
                  </MenuItem>
                  {vbcRatingKeys.map((key) => (
                    <MenuItem key={key} value={key}>
                      {t(`createPlayer.levels.${key}`)}
                    </MenuItem>
                  ))}
                </TextField>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <TextField
                  select
                  label={t('createPlayer.fields.projectionVbcRating', { clubName })}
                  variant="outlined"
                  value={projection || ''}
                  onChange={(e) => {
                    setProjection(e.target.value);
                    markDirty();
                  }}
                  InputProps={{
                    endAdornment: projection && (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            setProjection('');
                            markDirty();
                          }}
                          edge="end"
                          size="small"
                        >
                          <Clear />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.5,
                      '&:hover': {
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }
                    }
                  }}
                >
                  <MenuItem value="">
                    <em>{t('playerCardCreator.informeForm.selectProjection')}</em>
                  </MenuItem>
                  {vbcRatingKeys.map((key) => (
                    <MenuItem key={key} value={key}>
                      {t(`createPlayer.levels.${key}`)}
                    </MenuItem>
                  ))}
                </TextField>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <TextField
                  select
                  label={requiredLabel(t('playerCardCreator.informeForm.finalVerdict'))}
                  variant="outlined"
                  value={verdict || ''}
                  onChange={(e) => {
                    setVerdict(e.target.value);
                    markDirty();
                  }}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.5,
                      '&:hover': {
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }
                    }
                  }}
                >
                  <MenuItem value="">
                    <em>{t('playerCardCreator.informeForm.selectVerdict')}</em>
                  </MenuItem>
                  <MenuItem value="DESCARTADO">{t('playerCardCreator.informeForm.discarded')} ❌</MenuItem>
                  <MenuItem value="VOLVER A VER">{t('playerCardCreator.informeForm.watchAgain')} 🔄</MenuItem>
                  <MenuItem value="DUDAS">{t('playerCardCreator.informeForm.dudas')} ❓</MenuItem>
                  <MenuItem value="MONITORIZAR">{t('playerCardCreator.informeForm.monitor')} 👁️</MenuItem>
                  <MenuItem value="PRIORITARIO">{t('playerCardCreator.informeForm.priority')} ⭐</MenuItem>
                </TextField>
              </FormControl>
            </Grid>

            {/* FIT Club */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', mr: 1 }}>
                  FIT {clubName} :
                </Typography>
                {[
                  { key: 'fitEstilo', value: fitEstilo, setter: setFitEstilo, label: t('playerCardCreator.informeForm.fitEstilo') },
                  { key: 'fitFisico', value: fitFisico, setter: setFitFisico, label: t('playerCardCreator.informeForm.fitFisico') },
                  { key: 'fitActitud', value: fitActitud, setter: setFitActitud, label: t('playerCardCreator.informeForm.fitActitud') },
                  { key: 'fitMejora', value: fitMejora, setter: setFitMejora, label: t('playerCardCreator.informeForm.fitMejora') },
                  { key: 'fitContexto', value: fitContexto, setter: setFitContexto, label: t('playerCardCreator.informeForm.fitContexto') }
                ].map(({ key, value, setter, label }) => (
                  <Chip
                    key={key}
                    label={`${label}: ${value === true ? t('playerCardCreator.informeForm.fitYes') : value === false ? t('playerCardCreator.informeForm.fitNo') : '—'}`}
                    onClick={() => {
                      // Cycle: null -> true -> false -> null
                      if (value === null) setter(true);
                      else if (value === true) setter(false);
                      else setter(null);
                      markDirty();
                    }}
                    sx={{
                      cursor: 'pointer',
                      fontWeight: 600,
                      backgroundColor: value === true ? '#e8f5e9' : value === false ? '#ffebee' : '#f5f5f5',
                      color: value === true ? '#2e7d32' : value === false ? '#c62828' : '#9e9e9e',
                      border: '1px solid',
                      borderColor: value === true ? '#4caf50' : value === false ? '#f44336' : '#e0e0e0',
                      '&:hover': { opacity: 0.85 }
                    }}
                    size="small"
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Section 2bis: Defensive Versatility */}
        <Paper elevation={0} sx={compactSectionStyle}>
          <DefensiveVersatilityEditor
            value={defensiveVersatility}
            onChange={(next) => {
              setDefensiveVersatility(next);
              markDirty();
            }}
          />
        </Paper>

        {/* Section 3: Detalles - Horizontal layout */}
        <Paper elevation={0} sx={compactSectionStyle}>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Notes sx={iconStyle} />
                <Typography variant="h6" fontWeight={600} sx={{ fontSize: '1.1rem' }}>
                  {t('playerCardCreator.informeForm.observations')}
                  <Box component="span" sx={{ color: 'error.main', ml: 0.3 }}>*</Box>
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <SpeechToTextButton
                  backendUrl={backendUrl}
                  onTranscription={(transcript) => {
                    setDetalles((prev) => prev + '\n' + transcript);
                    markDirty();
                  }}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={9}>
              <Box sx={{
                '& .ql-container': {
                  borderRadius: '0 0 6px 6px',
                  minHeight: { xs: '100px', md: '120px' }
                },
                '& .ql-toolbar': {
                  borderRadius: '6px 6px 0 0',
                  background: 'rgba(255,255,255,0.9)'
                },
                '& .ql-editor': {
                  fontSize: '13px',
                  lineHeight: 1.5
                }
              }}>
                <ReactQuill
                  theme="snow"
                  value={detalles}
                  onChange={(value) => {
                    setDetalles(value);
                    markDirty();
                  }}
                  placeholder={t('playerCardCreator.informeForm.detailsPlaceholder')}
                  modules={quillModules}
                />
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Section 4: Media Upload */}
        <Paper elevation={0} sx={compactSectionStyle}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <AttachFile sx={iconStyle} />
            <Typography variant="h6" fontWeight={600} sx={{ fontSize: '1.1rem' }}>
              {t('playerCardCreator.informeForm.attachMedia')}
            </Typography>
            <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              ({t('playerCardCreator.informeForm.optional')})
            </Typography>
          </Box>

          {/* Input file caché */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept={MEDIA_ACCEPT_ATTR}
            style={{ display: 'none' }}
          />

          {!mediaFile ? (
            /* Zone de drop / upload */
            <Box
              ref={dropZoneRef}
              onClick={handleClickUpload}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              sx={{
                border: '2px dashed',
                borderColor: isDragging ? 'primary.main' : 'grey.300',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: isDragging
                  ? (theme) => alpha(theme.palette.primary.main, 0.05)
                  : 'grey.50',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.02)
                }
              }}
            >
              <CloudUpload sx={{ fontSize: 40, color: isDragging ? 'primary.main' : 'grey.400', mb: 1 }} />
              <Typography variant="body1" color="text.secondary" gutterBottom>
                {t('playerCardCreator.informeForm.dropOrClick')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('playerCardCreator.informeForm.pasteHint')}
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {MEDIA_FORMATS_LABEL}
                </Typography>
              </Box>
            </Box>
          ) : (
            /* Preview du fichier sélectionné */
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'grey.300',
                borderRadius: 2,
                p: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                backgroundColor: 'grey.50'
              }}
            >
              {/* Preview image ou icône PDF */}
              {previewUrl ? (
                <Box
                  component="img"
                  src={previewUrl}
                  alt="Preview"
                  sx={{
                    width: 80,
                    height: 80,
                    objectFit: 'cover',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'grey.200'
                  }}
                />
              ) : mediaFile?.type === 'application/pdf' ? (
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f44336',
                    borderRadius: 1
                  }}
                >
                  <PictureAsPdf sx={{ fontSize: 40, color: 'white' }} />
                </Box>
              ) : isHtmlFile(mediaFile) ? (
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#e67e22',
                    borderRadius: 1
                  }}
                >
                  <Code sx={{ fontSize: 40, color: 'white' }} />
                </Box>
              ) : (
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'grey.200',
                    borderRadius: 1
                  }}
                >
                  <ImageIcon sx={{ fontSize: 40, color: 'grey.500' }} />
                </Box>
              )}

              {/* Infos du fichier */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={500} noWrap>
                  {mediaFile.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {(mediaFile.size / 1024).toFixed(1)} KB
                </Typography>
              </Box>

              {/* Bouton supprimer */}
              <IconButton
                onClick={handleRemoveFile}
                size="small"
                sx={{
                  color: 'error.main',
                  '&:hover': {
                    backgroundColor: (theme) => alpha(theme.palette.error.main, 0.1)
                  }
                }}
              >
                <Delete />
              </IconButton>
            </Box>
          )}
        </Paper>

        {/* Save Button - Always visible, compact */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          mt: 2,
          mb: { xs: 2, md: 0 }
        }}>
          <Tooltip
            arrow
            title={hasMissingRequired
              ? `${t('playerCardCreator.informeForm.missingRequired')} ${missingRequired.join(', ')}`
              : ''}
          >
            <span>
              <Button
                variant="contained"
                onClick={handleCreateCard}
                disabled={isSubmitting || hasMissingRequired}
                startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <AssignmentTurnedIn />}
                sx={{
                  background: (isSubmitting || hasMissingRequired)
                    ? 'rgba(0,0,0,0.12)'
                    : 'linear-gradient(45deg, #FF8A65, #FF7043)',
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: '0 6px 20px rgba(255, 138, 101, 0.3)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: (isSubmitting || hasMissingRequired)
                      ? 'rgba(0,0,0,0.12)'
                      : 'linear-gradient(45deg, #FF7043, #FF5722)',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 8px 25px rgba(255, 138, 101, 0.4)'
                  },
                  '&:disabled': {
                    background: 'rgba(0,0,0,0.12)',
                    color: 'rgba(0,0,0,0.26)'
                  }
                }}
              >
                {isSubmitting ? t('playerCardCreator.informeForm.saving') : t('playerCardCreator.informeForm.saveReport')}
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

export default CreateInforme;